/**
 * The project-display-name join, as one hook.
 *
 * Mirrors `useProjectIconResolver` deliberately: same inputs, same shape, same
 * reason. A surface holds a workspace's ids and its checkout-derived label; the
 * name a person chose for the project lives on the linked CMMD record. Each
 * surface doing that join itself is how the icon fell out of sync per surface
 * before, so the name shares one resolver from the start.
 */

import { useMemo } from "react";

import { useProjectLinkListSnapshot } from "~/cmmd/projectLinkListResource";
import { useCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import {
  buildProjectDisplayNameByProjectKey,
  resolveProjectDisplayName,
} from "./projectDisplayNameSource";

/**
 * Returns a stable resolver: a workspace's ids → the project's Forge display
 * name, or `undefined` when nothing has been chosen and the caller should keep
 * the label it already has.
 */
export function useProjectDisplayNameResolver(): (
  environmentId: string | null | undefined,
  projectId?: string | null | undefined,
) => string | undefined {
  const linkSnapshot = useProjectLinkListSnapshot();
  const intakeSnapshot = useCmmdWorkIntakeSnapshot();

  const nameByProjectKey = useMemo(
    () => buildProjectDisplayNameByProjectKey(linkSnapshot.links, intakeSnapshot.projects),
    [linkSnapshot.links, intakeSnapshot.projects],
  );

  return useMemo(
    () => (environmentId, projectId) =>
      resolveProjectDisplayName(environmentId, projectId, nameByProjectKey),
    [nameByProjectKey],
  );
}
